import React, { useState } from 'react';
import { Lock, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

const CEO_AUTH_KEY = 'ceo_auth';
const CEO_SESSION_HOURS = 24;

function getStoredSession(): boolean {
  try {
    const raw = sessionStorage.getItem(CEO_AUTH_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as { t: number };
    const age = Date.now() - data.t;
    if (age > CEO_SESSION_HOURS * 60 * 60 * 1000) {
      sessionStorage.removeItem(CEO_AUTH_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function setCeoSession(): void {
  sessionStorage.setItem(CEO_AUTH_KEY, JSON.stringify({ t: Date.now() }));
}

export function clearCeoSession(): void {
  sessionStorage.removeItem(CEO_AUTH_KEY);
}

export function hasValidCeoSession(): boolean {
  return getStoredSession();
}

const GATE_PASSWORD = (import.meta.env.VITE_CLIENT_ACCESS_KEY ?? '').trim();

interface CEOGateLoginProps {
  onSuccess: () => void;
  onBack?: () => void;
}

export const CEOGateLogin: React.FC<CEOGateLoginProps> = ({ onSuccess, onBack }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!GATE_PASSWORD) {
      setError(true);
      return;
    }
    setLoading(true);
    setError(false);
    setTimeout(() => {
      if (password.trim() === GATE_PASSWORD) {
        setCeoSession();
        onSuccess();
      } else {
        setError(true);
        setPassword('');
        setLoading(false);
      }
    }, 400);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950 p-4">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="absolute left-4 top-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
          aria-label="Zurück zur Startseite"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück
        </button>
      )}
      <div className="w-full max-w-sm">
        <div className="rounded-2xl border border-white/10 bg-slate-900/90 p-8 shadow-2xl backdrop-blur-sm">
          <div className="mb-6 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-slate-700 bg-slate-800">
              <Lock className="h-7 w-7 text-blue-400" />
            </div>
          </div>
          <h1 className="mb-1 text-center text-xl font-bold text-white">Dashboard-Anmeldung</h1>
          <p className="mb-6 text-center text-xs text-slate-400">
            Nur für berechtigte Nutzer. Access Key eingeben.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Access Key"
              className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
              autoComplete="current-password"
              disabled={loading}
            />
            {error && (
              <p className="flex items-center gap-2 text-sm text-red-400">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                Ungültiger Schlüssel. Zugriff verweigert.
              </p>
            )}
            {!GATE_PASSWORD && (
              <p className="text-xs text-amber-500">
                Zugang nicht konfiguriert (VITE_CLIENT_ACCESS_KEY fehlt).
              </p>
            )}
            <button
              type="submit"
              disabled={loading || !GATE_PASSWORD}
              className="flex w-full items-center justify-center rounded-xl bg-blue-600 py-3 font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Anmelden'}
            </button>
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-600 py-2.5 text-sm text-slate-400 transition-colors hover:border-slate-500 hover:text-slate-300"
              >
                <ArrowLeft className="h-4 w-4" /> Zurück zur Startseite
              </button>
            )}
          </form>
        </div>
        <p className="mt-4 text-center text-[10px] text-slate-600">
          ARKORBIT AI · Nur autorisiertes Personal
        </p>
      </div>
    </div>
  );
};

export default CEOGateLogin;
